import { useMutation, useQuery } from "@apollo/client/react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/solid";
import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorFallback, Head } from "../../../../../components/core";
import { Button } from "../../../../../components/form";
import { LimitBy, TableCursor } from "../../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../../contexts/NotifyContext";
import {
  ADD_VEHICLESERVICETASK,
  EDIT_VEHICLESERVICETASK,
  GET_VEHICLESERVICETASKS,
  REMOVE_VEHICLESERVICETASK,
  type ServiceTask as RawServiceTask,
} from "../../../../../graphql/fleets/vehicles/sections/service-tasks";
import { AlertModal, AlertType, useAlert } from "../../../../../hooks/useAlert";
import { classNames, formatDate, toNestedTable } from "../../../../../utils";
import { AuthShield } from "../../../../auth/core";
import Form from "./components/Form";

type RowServiceTask = RawServiceTask & {
  subRows: RowServiceTask[];
};

const ServiceTaskList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const { t } = useTranslation();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { data, loading, error, refetch } = useQuery(GET_VEHICLESERVICETASKS);
  const serviceTasks: RowServiceTask[] = useMemo(() => {
    const items: RowServiceTask[] = data?.fetchServiceTasks.flatMap(
      (item: RawServiceTask) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })
    );
    const updatedItems = toNestedTable(items) as RowServiceTask[];
    return updatedItems || [];
  }, [data?.fetchServiceTasks]);

  const [activeServiceTask, setActiveServiceTask] = useState<
    RowServiceTask | undefined
  >(undefined);
  const [newServiceTask, setNewServiceTask] = useState<boolean | undefined>(
    undefined
  );

  const [updateServiceTask] = useMutation(EDIT_VEHICLESERVICETASK, {
    refetchQueries: [{ query: GET_VEHICLESERVICETASKS }],
  });
  const handleUpdate = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    updateServiceTask({
      variables: {
        id: activeServiceTask?.id,
        name: values.name,
        handle: values.handle,
        description: values.description,
        parentId: values.parent ? parseInt(values.parent.value) : null,
        imageUrl: values.imageUrl,
        status: String(values.status) === "true" ? true : false,
      },
    })
      .then(async ({ data }) => {
        actions.setSubmitting(false);
        if (data?.vehicleServiceTaskUpdate) {
          await refetch();
          setActiveServiceTask(undefined);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "ServiceTask update failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "ServiceTask update failed",
          message: error.message,
        });
      });
  };

  const [createServiceTask] = useMutation(ADD_VEHICLESERVICETASK, {
    refetchQueries: [{ query: GET_VEHICLESERVICETASKS }],
  });
  const handleCreate = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    createServiceTask({
      variables: {
        name: values.name,
        handle: values.handle,
        description: values.description,
        parentId: values.parent ? parseInt(values.parent.value) : null,
        imageUrl: values.imageUrl,
        status: String(values.status) === "true" ? true : false,
      },
    })
      .then(async ({ data }) => {
        actions.setSubmitting(false);
        if (data?.vehicleServiceTaskCreate) {
          await refetch();
          setNewServiceTask(undefined);
          addNotify({
            type: NotifyType.SUCCESS,
            title: "ServiceTask created successfully",
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "ServiceTask create failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "ServiceTask create failed",
          message: error.message,
        });
      });
  };

  const [deleteServiceTask] = useMutation(REMOVE_VEHICLESERVICETASK, {
    refetchQueries: [{ query: GET_VEHICLESERVICETASKS }],
  });
  const handleDelete = async (id: string) => {
    deleteServiceTask({
      variables: {
        id: Number(id),
      },
    })
      .then(({ data }) => {
        if (data?.vehicleServiceTaskDelete) {
          const deletedServiceTask =
            data.vehicleServiceTaskDelete?.vehicleServiceTask;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `ServiceTask ${deletedServiceTask.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "ServiceTask deletion failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "ServiceTask deletion failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<RowServiceTask, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        id: "expand",
        header: ({ table }) => (
          <button
            {...{
              onClick: table.getToggleAllRowsExpandedHandler(),
            }}
          >
            {table.getIsAllRowsExpanded() ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <Fragment>
            {row.getCanExpand() ? (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: "pointer" },
                }}
              >
                {row.getIsExpanded() ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            ) : (
              ""
            )}
          </Fragment>
        ),
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.name,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-serviceTasks"]}>
              <Button
                variant="icon"
                className="text-blue-500 hover:text-blue-600"
                onClick={() => setActiveServiceTask(props.row.original)}
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-serviceTasks"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id!,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon
                  aria-hidden="true"
                  className="text-md h-4 w-4 text-current"
                />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
      },
    ],
    []
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={ServiceTaskListResource.name}
        heading={ServiceTaskListResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: ServiceTaskListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {ServiceTaskListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {ServiceTaskListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-serviceTasks"]}>
              <Button onClick={() => setNewServiceTask(true)}>
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add service task
              </Button>
            </AuthShield>
            <Button
              variant="icon"
              onClick={handleRefetch}
              disabled={loading}
              className="flex h-9 items-center justify-center px-2 text-blue-700"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={classNames(
                  "h-5 w-5",
                  refetching ? "animate-spin" : ""
                )}
              />
              <span className="sr-only">Refresh service task list</span>
            </Button>
          </div>
        </div>

        <TableCursor
          data={serviceTasks}
          columns={columns}
          loading={loading}
          totalRows={serviceTasks.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          state={{
            expanded,
          }}
          enableRowSelection={false}
          onExpandedChange={setExpanded}
          getExpandedRowModel={getExpandedRowModel()}
          getSubRows={(row) => row.subRows}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No service tasks found"
          fallbackText="You can add a new service task by clicking on add service task button."
        />
      </div>

      <Transition.Root
        show={activeServiceTask ? true : false}
        as={Fragment}
        // afterLeave={() => setQuery("")}
        appear
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setActiveServiceTask(undefined);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-gray-100 rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <Form
                  heading="Edit ServiceTask - "
                  initialValues={{
                    id: activeServiceTask?.id,
                    name: activeServiceTask?.name ?? "",
                    description: activeServiceTask?.description ?? "",
                    parentId: activeServiceTask?.parent
                      ? parseInt(activeServiceTask.parent.id)
                      : null,
                    status: activeServiceTask?.status ? true : false,
                  }}
                  onSubmit={handleUpdate}
                  submitLabel={t("text_update")}
                  onCancel={() => setActiveServiceTask(undefined)}
                  cancelLabel={t("text_cancel")}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root
        show={newServiceTask ? true : false}
        as={Fragment}
        appear
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setNewServiceTask(undefined);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-gray-100 rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <Form
                  heading="New ServiceTask - "
                  initialValues={{
                    name: "",
                    description: "",
                    parentId: null,
                    status: true,
                  }}
                  onSubmit={handleCreate}
                  submitLabel={t("text_create")}
                  onCancel={() => setNewServiceTask(undefined)}
                  cancelLabel={t("text_cancel")}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ServiceTaskList;
export const ServiceTaskListResource: ResourceProps = {
  name: "Service Tasks",
  description: "A list of service tasks",
  access: ["create-serviceTasks", "read-serviceTasks"],
  path: "service-tasks",
};
